import React, { useId } from "react";
import { SvgIcon } from "@mui/material";

/**
 * MatchIQ assistant icon.
 *
 * A vector redraw of "Chat Icon.png" (the MatchIQ mascot): a blue disc with
 * cyan highlight arcs, a white robot waving from inside it, and a speech bubble
 * top-right. The same artwork lives as a standalone file at
 * `assets/matchiq-icon.svg` - keep the two in step if you edit either.
 *
 * Why a redraw rather than the PNG: the original is a 1.4 MB raster that is
 * soft at the 16-20 px used in the chat title bar and blurry in the 160 px
 * expanded view. These are flat shapes plus four linear gradients, so it is a
 * few hundred bytes in the bundle and crisp at every size.
 *
 * Built on MUI's SvgIcon so it inherits `fontSize` and `sx` exactly like the
 * @mui/icons-material icons used elsewhere in the app. It is deliberately
 * full-colour rather than `currentColor`, because it is a brand mark - place it
 * on a white or very light surface, not on the blue header gradient.
 *
 * Gradient ids are suffixed with React's `useId()` so several instances
 * (launcher, title bar, greeting avatar) can be mounted at once without the
 * duplicate-id collisions that would make one of them render flat.
 */
export default function AssistantIcon({ animated = false, ...props }) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const ring = `matchiqRing-${uid}`;
  const disc = `matchiqDisc-${uid}`;
  const shell = `matchiqShell-${uid}`;
  const visor = `matchiqVisor-${uid}`;

  return (
    <SvgIcon viewBox="0 0 64 64" titleAccess="MatchIQ assistant" {...props}>
      <defs>
        <linearGradient id={disc} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#2E7BF6" />
          <stop offset="0.55" stopColor="#0B4FD6" />
          <stop offset="1" stopColor="#06287F" />
        </linearGradient>
        <linearGradient id={shell} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#FFFFFF" />
          <stop offset="1" stopColor="#DCE7F6" />
        </linearGradient>
        <linearGradient id={ring} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#4CD2FF" />
          <stop offset="0.5" stopColor="#1E88E5" />
          <stop offset="1" stopColor="#06287F" />
        </linearGradient>
        <linearGradient id={visor} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#20305C" />
          <stop offset="1" stopColor="#091128" />
        </linearGradient>
      </defs>

      {/* Outer ring, hairline gap, then the blue disc. The gap is what keeps
          the ring legible against the disc; without it the two blues merge. */}
      <circle cx="32" cy="32" r="31.6" fill={`url(#${ring})`} />
      <circle cx="32" cy="32" r="29.4" fill="#FFFFFF" />
      <circle cx="32" cy="32" r="28.6" fill={`url(#${disc})`} />

      {/* Cyan highlight arcs tracing the inside of the disc */}
      <g
        fill="none"
        stroke="#4CD2FF"
        strokeWidth="2.6"
        strokeLinecap="round"
        opacity="0.95"
      >
        <path d="M14.4 17.2A23 23 0 0 1 26.6 10.2" />
        <path d="M9.6 26.4A23 23 0 0 0 9.6 38.4" />
        <path d="M50.2 44.2A22 22 0 0 1 38.2 53.2" />
      </g>

      {/* Shoulder joints and arms, drawn behind the torso so only the joint shows */}
      <circle cx="20.2" cy="45.4" r="4" fill="#13203C" />
      <circle cx="43.8" cy="45.4" r="4" fill="#13203C" />
      <path
        d="M19.4 46.6 16.8 51.6"
        stroke={`url(#${shell})`}
        strokeWidth="5.4"
        strokeLinecap="round"
      />
      {/* Right arm and hand share a pivot so the intro mascot can wave. */}
      <g className={animated ? "matchiq-wave-arm" : undefined}>
        <path
          d="M43.6 45 49.6 37.6"
          stroke={`url(#${shell})`}
          strokeWidth="6.4"
          strokeLinecap="round"
        />

        <rect x="45" y="31.4" width="10.6" height="11" rx="5" fill={`url(#${shell})`} />
        <path
          d="M45 38.9h10.6v1.6a1.9 1.9 0 0 1-1.9 1.9h-6.8a1.9 1.9 0 0 1-1.9-1.9z"
          fill="#13203C"
        />
        <g stroke="#C3D4EA" strokeWidth="0.9" strokeLinecap="round">
          <path d="M47.9 32.9v5.1" />
          <path d="M50.3 32.6v5.4" />
          <path d="M52.7 33v5" />
        </g>
      </g>

      {/* Torso */}
      <path
        d="M19.8 52.6v-4.2c0-6.5 5.5-11.2 12.2-11.2s12.2 4.7 12.2 11.2v4.2z"
        fill={`url(#${shell})`}
      />
      {/* Chest spark */}
      <path
        d="M32 42.3l1.45 2.75 2.75 1.45-2.75 1.45L32 50.65l-1.45-2.75-2.75-1.45 2.75-1.45z"
        fill="#2E7BF6"
      />

      {/* Ears */}
      <circle cx="15.6" cy="26.2" r="4.6" fill="#13203C" />
      <circle
        cx="15.6"
        cy="26.2"
        r="2.5"
        fill="none"
        stroke="#36C6FF"
        strokeWidth="1.5"
      />
      <circle cx="47.4" cy="26.2" r="4.6" fill="#13203C" />
      <circle
        cx="47.4"
        cy="26.2"
        r="2.5"
        fill="none"
        stroke="#36C6FF"
        strokeWidth="1.5"
      />

      {/* Head */}
      <rect
        x="16.6"
        y="12.6"
        width="29.8"
        height="24"
        rx="10.6"
        fill={`url(#${shell})`}
      />
      <rect x="25.6" y="15.2" width="12.8" height="2.8" rx="1.4" fill="#36C6FF" />

      {/* Visor and smiling eyes */}
      <rect
        x="20.4"
        y="19.2"
        width="22.2"
        height="14.4"
        rx="7.2"
        fill={`url(#${visor})`}
      />
      <g fill="none" stroke="#3FE1FF" strokeWidth="2.3" strokeLinecap="round">
        <path d="M24.5 27.6c1.3 2.2 3.5 2.2 4.8 0" />
        <path d="M33.7 27.6c1.3 2.2 3.5 2.2 4.8 0" />
      </g>

      {/* Speech bubble */}
      <path
        d="M51.2 8.6h8.2a4.4 4.4 0 0 1 4.4 4.4v4.2a4.4 4.4 0 0 1-4.4 4.4h-3.9l-3.6 3.5v-3.5h-0.7a4.4 4.4 0 0 1-4.4-4.4V13a4.4 4.4 0 0 1 4.4-4.4z"
        fill="#D7F0FF"
      />
      <g fill="#1A73E8">
        <circle cx="51.6" cy="15.1" r="1.45" />
        <circle cx="55.7" cy="15.1" r="1.45" />
        <circle cx="59.8" cy="15.1" r="1.45" />
      </g>
    </SvgIcon>
  );
}
